import { useEffect, useState } from 'react';
import { API_URL, apiGet } from '../lib/api';

export interface WebsiteBlogPost {
  slug: string;
  title: string;
  excerpt?: string;
  body: string;
  author?: string;
  category?: string;
  tags?: string[];
  image_url?: string;
  featured?: boolean;
  published_at?: string;
}

const resolveImageUrl = (value?: string) => {
  if (!value) return value;
  if (value.startsWith('http')) return value;
  return `${API_URL}${value}`;
};

const normalizePost = (post: WebsiteBlogPost): WebsiteBlogPost => ({
  ...post,
  image_url: resolveImageUrl(post.image_url),
});

export function useBlogPosts() {
  const [posts, setPosts] = useState<WebsiteBlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    apiGet<{ success: boolean; posts: WebsiteBlogPost[] }>('/api/public/blog-posts')
      .then((response) => {
        if (alive) setPosts((response.posts || []).map(normalizePost));
      })
      .catch(() => {
        if (alive) setPosts([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  return { posts, loading };
}

export function useBlogPost(slug?: string) {
  const [post, setPost] = useState<WebsiteBlogPost | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    if (!slug) {
      setLoading(false);
      return () => {
        alive = false;
      };
    }

    apiGet<{ success: boolean; post: WebsiteBlogPost }>(`/api/public/blog-posts/${slug}`)
      .then((response) => {
        if (alive) setPost(response.post ? normalizePost(response.post) : null);
      })
      .catch(() => {
        if (alive) setPost(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [slug]);

  return { post, loading };
}
